import React, { useState, useEffect } from 'react';
import style from './index.module.css';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { collection, getDocs, deleteDoc, doc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { signInWithEmailAndPassword, onAuthStateChanged, signOut } from 'firebase/auth';
import { db, auth } from '../../utils/firebase';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import MyFooter from '../../components/MyFooter';

const Admin = () => {
    const theme = useTheme();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [user, setUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    const [inquiries, setInquiries] = useState([]);
    const [stories, setStories] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // New Client Form State
    const [newClient, setNewClient] = useState({
        name: '',
        sign: '',
        from: ''
    });

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setAuthLoading(false);
            if (currentUser) {
                fetchData();
            }
        });
        return () => unsubscribe();
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (error) {
            console.error('Login Error:', error);
            alert('Invalid credentials. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            setInquiries([]);
            setStories([]);
            setCustomers([]);
        } catch (error) {
            console.error('Logout Error:', error);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch Inquiries
            const inqSnapshot = await getDocs(collection(db, 'contact_inquiries'));
            setInquiries(inqSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                .sort((a, b) => b.timestamp?.seconds - a.timestamp?.seconds));
            
            // Fetch Stories
            const storiesSnapshot = await getDocs(collection(db, 'stories'));
            setStories(storiesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                .sort((a, b) => b.timestamp?.seconds - a.timestamp?.seconds));

            // Fetch Customers/Clients
            const custSnapshot = await getDocs(collection(db, 'customers'));
            setCustomers(custSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddClient = async (e) => {
        e.preventDefault();
        if (!newClient.name || !newClient.sign || !newClient.from) return;
        
        setLoading(true);
        try {
            const docRef = await addDoc(collection(db, 'customers'), {
                ...newClient,
                timestamp: serverTimestamp()
            });
            setCustomers([...customers, { id: docRef.id, ...newClient }]);
            setNewClient({ name: '', sign: '', from: '' });
            alert('Client added successfully!');
        } catch (error) {
            console.error('Error adding client:', error);
            alert('Failed to add client');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClient = async (id) => {
        if (window.confirm('Remove this client from the list?')) {
            try {
                await deleteDoc(doc(db, 'customers', id));
                setCustomers(customers.filter(c => c.id !== id));
            } catch (error) {
                console.error('Error deleting client:', error);
            }
        }
    };

    const handleDeleteInquiry = async (id) => {
        if (window.confirm('Are you sure you want to delete this inquiry?')) {
            try {
                await deleteDoc(doc(db, 'contact_inquiries', id));
                setInquiries(inquiries.filter(item => item.id !== id));
            } catch (error) {
                console.error('Error deleting inquiry:', error);
            }
        }
    };

    const handleStoryStatus = async (id, newStatus) => {
        try {
            await updateDoc(doc(db, 'stories', id), { status: newStatus });
            setStories(stories.map(s => s.id === id ? { ...s, status: newStatus } : s));
        } catch (error) {
            console.error('Error updating story status:', error);
        }
    };

    const handleDeleteStory = async (id) => {
        if (window.confirm('Delete this story permanently?')) {
            try {
                await deleteDoc(doc(db, 'stories', id));
                setStories(stories.filter(s => s.id !== id));
            } catch (error) {
                console.error('Error deleting story:', error);
            }
        }
    };

    if (authLoading) {
        return (
            <div className={style.loginContainer}>
                <CircularProgress />
            </div>
        );
    }

    if (!user) {
        return (
            <div className={style.loginContainer}>
                <motion.div 
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className={style.loginCard}
                >
                    <h1>Admin Login</h1>
                    <form onSubmit={handleLogin}>
                        <TextField
                            fullWidth
                            type="email"
                            label="Admin Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            type="password"
                            label="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            margin="normal"
                            required
                        />
                        <Button 
                            fullWidth 
                            variant="contained" 
                            type="submit" 
                            disabled={loading}
                            sx={{ mt: 2, py: 1.5 }}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Login'}
                        </Button>
                    </form>
                </motion.div>
            </div>
        );
    }

    return (
        <div className={style.adminContainer}>
            <div className={style.adminHeader}>
                <h1>Admin Dashboard</h1>
                <Button variant="outlined" onClick={handleLogout}>Logout</Button>
            </div>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
                <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)}>
                    <Tab label="Contact Inquiries" />
                    <Tab label="Story Moderation" />
                    <Tab label="Manage Clients" />
                </Tabs>
            </Box>

            {tabValue === 0 && (
                <div className={style.inquiryList}>
                    {loading && inquiries.length === 0 ? (
                        <CircularProgress />
                    ) : inquiries.length === 0 ? (
                        <p>No inquiries yet.</p>
                    ) : (
                        inquiries.map((item) => (
                            <motion.div 
                                key={item.id} 
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                className={style.inquiryCard}
                            >
                                <div className={style.inquiryHeader}>
                                    <h3>{item.name}</h3>
                                    <Button 
                                        color="error" 
                                        startIcon={<DeleteIcon />}
                                        onClick={() => handleDeleteInquiry(item.id)}
                                    >
                                        Delete
                                    </Button>
                                </div>
                                <p><strong>Email:</strong> {item.email}</p>
                                <p><strong>Phone:</strong> {item.phone}</p>
                                <p className={style.message}><strong>Message:</strong> {item.message}</p>
                                <span className={style.date}>
                                    {item.timestamp?.toDate().toLocaleString() || 'No date'}
                                </span>
                            </motion.div>
                        ))
                    )}
                </div>
            )}

            {tabValue === 1 && (
                <div className={style.storyModeration}>
                    {loading && stories.length === 0 ? (
                        <CircularProgress />
                    ) : stories.length === 0 ? (
                        <p>No stories submitted yet.</p>
                    ) : (
                        stories.map((item) => (
                            <div key={item.id} className={style.modCard}>
                                <div className={style.modHeader}>
                                    <h3>{item.eventTitle} <span className={style.statusTag} data-status={item.status}>{item.status}</span></h3>
                                    <div className={style.modActions}>
                                        {item.status === 'pending' ? (
                                            <Button variant="contained" color="success" size="small" onClick={() => handleStoryStatus(item.id, 'approved')}>Approve</Button>
                                        ) : (
                                            <Button variant="outlined" size="small" onClick={() => handleStoryStatus(item.id, 'pending')}>Move to Pending</Button>
                                        )}
                                        <IconButton color="error" onClick={() => handleDeleteStory(item.id)}><DeleteIcon /></IconButton>
                                    </div>
                                </div>
                                <p><strong>Author:</strong> {item.name}</p>
                                <p className={style.modStory}>{item.story}</p>
                            </div>
                        ))
                    )}
                </div>
            )}

            {tabValue === 2 && (
                <div className={style.clientManagement}>
                    <form onSubmit={handleAddClient} className={style.addClientForm}>
                        <h2>Add New Client</h2>
                        <div className={style.formGrid}>
                            <TextField
                                label="Client Name"
                                value={newClient.name}
                                onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                                required
                            />
                            <TextField
                                label="Signature (e.g., K.T)"
                                value={newClient.sign}
                                onChange={(e) => setNewClient({...newClient, sign: e.target.value})}
                                required
                            />
                            <TextField
                                label="Location (e.g., Mumbai)"
                                value={newClient.from}
                                onChange={(e) => setNewClient({...newClient, from: e.target.value})}
                                required
                            />
                            <Button 
                                variant="contained" 
                                type="submit" 
                                startIcon={<AddIcon />}
                                disabled={loading}
                            >
                                Add Client
                            </Button>
                        </div>
                    </form>

                    <div className={style.clientGrid}>
                        {customers.map((item) => (
                            <div key={item.id} className={style.clientAdminCard}>
                                <div>
                                    <h4>{item.name}</h4>
                                    <p>{item.sign} | {item.from}</p>
                                </div>
                                <IconButton color="error" onClick={() => handleDeleteClient(item.id)}>
                                    <DeleteIcon />
                                </IconButton>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            
            <MyFooter />
        </div>
    );
};

export default Admin;
